import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { applyMode, Mode } from "@amzn/awsui-global-styles";
import { DARK_THEME, LIGHT_THEME } from "../constants";

const DarkModeContext = createContext(false);

export interface ThemeToggle {
  checked: boolean;
  handleThemeChange: (checked: boolean) => void;
}

export const ThemeToggleContext = createContext<ThemeToggle>({
  checked: false,
  handleThemeChange: () => {},
});

/**
 * Owns the app's light/dark mode, so every page starts in the stored theme and keeps it across
 * navigation instead of each page root holding its own copy.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [darkMode, setDarkMode] = useState(
    () => window.localStorage.getItem("theme") === DARK_THEME
  );

  useEffect(() => {
    window.localStorage.setItem("theme", darkMode ? DARK_THEME : LIGHT_THEME);
    applyMode(darkMode ? Mode.Dark : Mode.Light);
  }, [darkMode]);

  const handleThemeChange = useCallback(
    (checked: boolean) => setDarkMode(checked),
    []
  );
  const toggle = useMemo(
    () => ({ checked: darkMode, handleThemeChange }),
    [darkMode, handleThemeChange]
  );

  return (
    <DarkModeContext.Provider value={darkMode}>
      <ThemeToggleContext.Provider value={toggle}>
        {children}
      </ThemeToggleContext.Provider>
    </DarkModeContext.Provider>
  );
}

export default DarkModeContext;
